import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Upload } from "lucide-react";
import { api, API, errMsg, money } from "@/lib/api";
import { StatTile } from "@/components/Bits";

const ClaimPhoto = ({ j, isAdmin, onChanged }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/higher/joiners/${j.id}/photo`, fd);
      toast.success("Claim photo uploaded");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't upload photo"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="higher-claim-photo" className="rounded-2xl border border-white/10 bg-zinc-950/60 p-3 flex flex-col">
      <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Claim Photo</div>
      <div className="mt-2 flex-1 min-h-[140px] rounded-xl bg-zinc-900 grid place-items-center overflow-hidden">
        {j.photo_version ? (
          <a href={`${API}/higher/joiners/${j.id}/photo?v=${j.photo_version}`} target="_blank" rel="noreferrer" data-testid="higher-claim-photo-link" className="block h-full w-full">
            <img src={`${API}/higher/joiners/${j.id}/photo?v=${j.photo_version}`} alt={`${j.name} claim`} data-testid="higher-claim-photo-image" className="h-full max-h-56 w-full object-contain" />
          </a>
        ) : <div data-testid="higher-claim-photo-empty" className="text-center text-xs text-zinc-500 p-4"><ImagePlus className="h-6 w-6 mx-auto mb-1" />No photo yet</div>}
      </div>
      {isAdmin && (
        <>
          <input ref={input} type="file" accept="image/*" className="hidden" data-testid="higher-claim-photo-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          <button type="button" data-testid="higher-claim-photo-upload" disabled={busy} onClick={() => input.current?.click()}
            className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-xl border border-sky-400/40 px-3 py-2 text-xs font-semibold text-sky-200 transition-colors hover:bg-sky-500/10 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {j.photo_version ? "Replace photo" : "Upload photo"}
          </button>
        </>
      )}
    </div>
  );
};

export default function HigherSummaryBox({ j, isAdmin, onChanged }) {
  return (
    <div data-testid="higher-summary-box" className="mt-6 grid gap-3 sm:grid-cols-[1fr_240px] rounded-3xl border border-white/10 bg-zinc-900/50 p-3 sm:p-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 content-start">
        <StatTile label="Total # of PCs" value={j.total_missing ? <>{j.total_pcs}<span data-testid="higher-total-missing" className="block text-xs font-sans font-normal text-red-300">{j.total_missing} missing</span></> : j.total_pcs} testId="higher-total-pcs" />
        <StatTile label="Total EMS Due" value={money(j.ems_left)} accent="text-pink-300" testId="higher-total-ems-due" />
        <div className="col-span-2 sm:col-span-1"><StatTile label="Total EMS Paid" value={money(j.ems_paid)} accent="text-emerald-300" testId="higher-total-ems-paid" /></div>
      </div>
      <ClaimPhoto j={j} isAdmin={isAdmin} onChanged={onChanged} />
    </div>
  );
}
